import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { MessageSquare, Loader2, Plus, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nProvider";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Messages() {
  const t = useT();
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [newContact, setNewContact] = useState("");
  const endRef = useRef(null);

  const loadConvos = useCallback(() => {
    api.get("/messaging/conversations").then((r) => setConvos(r.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { loadConvos(); api.get("/messaging/contacts").then((r) => setContacts(r.data)).catch(() => {}); }, [loadConvos]);
  useEffect(() => { const t = setInterval(loadConvos, 5000); return () => clearInterval(t); }, [loadConvos]);

  const openConvo = useCallback((c) => {
    setActive(c);
    api.get(`/messaging/conversations/${c.id}/messages`).then((r) => setMsgs(r.data));
    api.post(`/messaging/conversations/${c.id}/read`).then(loadConvos);
  }, [loadConvos]);

  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => api.get(`/messaging/conversations/${active.id}/messages`).then((r) => setMsgs(r.data)), 4000);
    return () => clearInterval(t);
  }, [active]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if (!text.trim() || !active) return;
    try {
      await api.post(`/messaging/conversations/${active.id}/messages`, { body: text });
      setText(""); const r = await api.get(`/messaging/conversations/${active.id}/messages`); setMsgs(r.data); loadConvos();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const startConvo = async () => {
    if (!newContact) return;
    try { const { data } = await api.post("/messaging/conversations", { participant_ids: [newContact], is_group: false });
      setOpen(false); setNewContact(""); await loadConvos(); openConvo({ id: data.id, title: contacts.find((c) => c.id === newContact)?.full_name });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="p-6 sm:p-8 max-w-[1200px]">
      <PageHeader title={t("messages.title")} subtitle={t("messages.subtitle")}>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-conversation-button" className="bg-navy hover:bg-navy-soft text-white gap-2"><Plus className="h-4 w-4" />{t("messages.new")}</Button></DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle className="font-heading">{t("messages.newConversation")}</DialogTitle></DialogHeader>
            <Select value={newContact} onValueChange={setNewContact}>
              <SelectTrigger data-testid="contact-select"><SelectValue placeholder={t("messages.selectColleague")} /></SelectTrigger>
              <SelectContent>{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name} · {c.role.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
            <DialogFooter><Button onClick={startConvo} data-testid="start-conversation" className="bg-navy text-white">{t("messages.start")}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[70vh]">
        <div className="bg-white rounded-xl border border-slate-200 overflow-y-auto" data-testid="conversation-list">
          {loading ? <div className="p-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-navy" /></div>
            : convos.length === 0 ? <div className="p-8 text-center text-sm text-slate-400">{t("messages.noConversations")}</div>
            : convos.map((c) => (
              <button key={c.id} onClick={() => openConvo(c)} data-testid={`conversation-${c.id}`}
                className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 ${active?.id === c.id ? "bg-blue-50" : ""}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-800 truncate">{c.title}</span>
                  {c.unread > 0 && <span className="ml-2 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center" data-testid={`convo-unread-${c.id}`}>{c.unread}</span>}
                </div>
                <p className="text-xs text-slate-400 truncate">{c.last_message || t("messages.noMessages")}</p>
              </button>
            ))}
        </div>

        <div className="md:col-span-2 bg-white rounded-xl border border-slate-200 flex flex-col">
          {!active ? <div className="flex-1 flex items-center justify-center text-slate-400"><MessageSquare className="h-6 w-6 mr-2" />{t("messages.selectConversation")}</div>
            : (<>
              <div className="px-4 py-3 border-b border-slate-100 font-medium text-navy">{active.title}</div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="message-thread">
                {msgs.map((m) => (
                  <div key={m.id} className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${m.mine ? "ml-auto bg-navy text-white" : "bg-slate-100 text-slate-800"}`}>
                    {!m.mine && <p className="text-[11px] opacity-70 mb-0.5">{m.sender_name}</p>}
                    {m.body}
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <div className="p-3 border-t border-slate-100 flex gap-2">
                <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={t("messages.typeMessage")} data-testid="message-input" />
                <Button onClick={send} data-testid="send-message-button" className="bg-navy text-white"><Send className="h-4 w-4" /></Button>
              </div>
            </>)}
        </div>
      </div>
    </div>
  );
}
